import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import Swal from 'sweetalert2'
import Lang from '../../config/lang'
import { isApplicationHistory } from '../../config/applicationProgress'
import EmptyAsset from '../profile/profileForms/emptyAsset'
import {
  loadHolidayDates,
  loadMyCandidateJobOffer,
  loadMyApplicationDetail,
  loadMyApplicationOverview,
  requestMyApplicationReschedule,
  submitMyApplicationSchedule,
} from './api'
import ActiveApplicationRenderer from './components/ActiveApplicationRenderer'
import HistoryApplicationRenderer from './components/HistoryApplicationRenderer'
import RescheduleModal from './components/RescheduleModal'
import {
  mapCandidateToMyApplicationOverview,
  mapInterviewBookingToMyApplication,
} from './applicationMapper'

const EmptyState = ({ title, desc }) => (
  <div className='rounded-[24px] border border-dashed border-[#DBEAFE] bg-white'>
    <EmptyAsset
      title={title}
      desc={desc}
      iconAsset='/asset/icon/empty_jobs.svg'
    />
  </div>
)

const showStatusAlert = ({ title, text, icon, timer }) =>
  Swal.fire({
    title,
    text,
    icon,
    timer,
    showConfirmButton: false,
    customClass: { popup: 'my-custom-popup' },
  })

const isPendingJobOfferApplication = (application) =>
  application?.displayStatus === 'Job Offer' &&
  application?.isOffer !== true &&
  application?.isOffer !== false

const getFirstAvailableSlot = (day) =>
  day?.slots?.find((slot) => !slot.isFull) ?? day?.slots?.[0] ?? null

const toDateParam = (value) => {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const toDateKey = (value) => {
  const parsedDate = new Date(value)

  if (Number.isNaN(parsedDate.getTime())) return ''

  return `${parsedDate.getFullYear()}-${String(
    parsedDate.getMonth() + 1,
  ).padStart(2, '0')}-${String(parsedDate.getDate()).padStart(2, '0')}`
}

const normalizeHolidayDateSet = (holidayDates = []) =>
  new Set(
    holidayDates
      .map((item) => toDateKey(item))
      .filter((item) => String(item).trim() !== ''),
  )

const isWeekendDate = (value) => {
  const parsedDate = new Date(value)

  if (Number.isNaN(parsedDate.getTime())) return false

  const day = parsedDate.getDay()
  return day === 0 || day === 6
}

const isSelectableScheduleDay = (day, holidayDateSet) => {
  if (!day?.date) return false

  const hasAvailableSlots =
    Array.isArray(day?.slots) && day.slots.some((slot) => !slot?.isFull)

  if (!hasAvailableSlots) return false

  const dateKey = toDateKey(day.date)

  return (
    dateKey !== '' && !holidayDateSet.has(dateKey) && !isWeekendDate(day.date)
  )
}

const getFirstSelectableScheduleDay = (application, holidayDateSet) =>
  application?.interview?.schedule?.find((day) =>
    isSelectableScheduleDay(day, holidayDateSet),
  ) || null

const getInterviewInvitationErrorState = (message, lang) => {
  const messageText = String(message || '')
    .trim()
    .toLowerCase()

  if (messageText.includes('expired')) {
    return {
      title: Lang(lang, 'interview_invitation_expired_title'),
      desc: Lang(lang, 'interview_invitation_expired_desc'),
    }
  }

  if (
    messageText.includes('already been used') ||
    messageText.includes('already used')
  ) {
    return {
      title: Lang(lang, 'interview_invitation_used_title'),
      desc: Lang(lang, 'interview_invitation_used_desc'),
    }
  }

  if (
    messageText.includes('still pending') ||
    messageText.includes('being prepared') ||
    messageText.includes('wait for the email update')
  ) {
    return {
      title: Lang(lang, 'interview_invitation_waiting_title'),
      desc: Lang(lang, 'interview_invitation_waiting_desc'),
    }
  }

  if (messageText.includes('no interview slot is available')) {
    return {
      title: Lang(lang, 'no_interview_schedule_available'),
      desc: Lang(lang, 'no_interview_schedule_available_desc'),
    }
  }

  if (
    messageText.includes('no longer available because all interview slots') ||
    messageText.includes('moved to reschedule status')
  ) {
    return {
      title: Lang(lang, 'interview_reschedule_waiting_title'),
      desc: Lang(lang, 'interview_reschedule_waiting_desc'),
    }
  }

  return null
}

const MyApplicationContent = () => {
  const { lang } = useSelector((state) => state.globalReducer)

  const [activeTab, setActiveTab] = useState('active')
  const [applications, setApplications] = useState([])
  const [selectedDateById, setSelectedDateById] = useState({})
  const [selectedSlotIdById, setSelectedSlotIdById] = useState({})
  const [holidayDates, setHolidayDates] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [errorState, setErrorState] = useState(null)
  const [submittingApplicationId, setSubmittingApplicationId] = useState('')
  const [reschedulingApplicationId, setReschedulingApplicationId] = useState('')
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false)
  const [activeApplication, setActiveApplication] = useState(null)
  const submittingApplicationRef = useRef('')
  const reschedulingApplicationRef = useRef('')

  const params = new URLSearchParams(window.location.search)
  const interviewInvitationID =
    Number(
      params.get('InterviewInvitationID') ||
        params.get('interviewInvitationID') ||
        params.get('invitationId') ||
        '',
    ) || 0
  const hasInterviewInvitationID = interviewInvitationID > 0
  const userToken = localStorage?.getItem('authToken') || ''

  const activeApplications = applications.filter(
    (application) => !isApplicationHistory(application),
  )
  const historyApplications = applications.filter((application) =>
    isApplicationHistory(application),
  )
  const visibleApplications =
    activeTab === 'history' ? historyApplications : activeApplications
  const sectionTitle =
    activeTab === 'history'
      ? Lang(lang, 'history')
      : Lang(lang, 'active_applications')
  const emptyStateContent = hasInterviewInvitationID
    ? {
        title: Lang(lang, 'no_interview_schedule_available'),
        desc: Lang(lang, 'no_interview_schedule_available_desc'),
      }
    : activeTab === 'history'
      ? {
          title: Lang(lang, 'no_history_applications'),
          desc: Lang(lang, 'no_history_applications_desc'),
        }
      : activeTab === 'active'
        ? {
            title: Lang(lang, 'no_active_applications'),
            desc: Lang(lang, 'no_active_applications_desc'),
          }
        : {
            title: Lang(lang, 'no_active_applications'),
            desc: Lang(lang, 'no_active_applications_desc'),
          }

  const applyDefaultSelections = useCallback((nextApplications) => {
    const nextSelectedDateById = {}
    const nextSelectedSlotIdById = {}

    nextApplications.forEach((application) => {
      const schedule = (application?.interview?.schedule || []).filter(
        (day) => !isWeekendDate(day?.date),
      )
      const defaultDay =
        schedule.find(
          (day) =>
            Array.isArray(day?.slots) &&
            day.slots.some((slot) => !slot?.isFull),
        ) || null
      const defaultSlot =
        defaultDay?.slots?.find((slot) => slot.isRecommended && !slot.isFull) ||
        getFirstAvailableSlot(defaultDay)

      nextSelectedDateById[application.id] = defaultDay?.date || ''
      nextSelectedSlotIdById[application.id] = defaultSlot?.id || ''
    })

    setSelectedDateById(nextSelectedDateById)
    setSelectedSlotIdById(nextSelectedSlotIdById)
  }, [])

  const loadApplications = useCallback(async () => {
    setIsLoading(true)

    let nextApplications = []
    let nextErrorState = null

    if (hasInterviewInvitationID) {
      if (!userToken) {
        nextErrorState = {
          title: 'Oops!',
          desc: Lang(lang, 'applications_login_required'),
        }
      } else {
        let detailResponse = null
        let detailErrorMessage = ''

        try {
          detailResponse = await loadMyApplicationDetail({
            interviewInvitationID,
            userToken,
          })
        } catch (error) {
          detailErrorMessage = error?.message || ''
        }

        const overviewResponse = userToken
          ? await loadMyApplicationOverview({
              userToken,
            })
          : null
        const overviewItem = Array.isArray(overviewResponse)
          ? overviewResponse.find(
              (item) =>
                Number(item?.interviewInvitationID) === interviewInvitationID,
            ) || null
          : null
        const overviewApplication = overviewItem
          ? mapCandidateToMyApplicationOverview(overviewItem, lang)
          : null

        if (detailErrorMessage) {
          if (overviewApplication?.interviewInvitationIsReschedule === true) {
            nextApplications = [overviewApplication]
          } else {
            nextErrorState = getInterviewInvitationErrorState(
              detailErrorMessage,
              lang,
            ) || {
              title: 'Oops!',
              desc: Lang(lang, 'interview_booking_load_failed'),
            }
          }
        } else if (!detailResponse) {
          if (overviewApplication?.interviewInvitationIsReschedule === true) {
            nextApplications = [overviewApplication]
          } else {
            nextErrorState = {
              title: 'Oops!',
              desc: Lang(lang, 'interview_booking_load_failed'),
            }
          }
        } else {
          const detailApplication = mapInterviewBookingToMyApplication(
            detailResponse,
            lang,
            interviewInvitationID,
          )[0]

          if (!detailApplication) {
            nextApplications = overviewApplication ? [overviewApplication] : []
          } else if (overviewApplication) {
            nextApplications = [
              {
                ...overviewApplication,
                ...detailApplication,
                id: overviewApplication?.id || detailApplication?.id,
                title: overviewApplication?.title || detailApplication?.title,
                company:
                  overviewApplication?.company || detailApplication?.company,
                status:
                  overviewApplication?.status || detailApplication?.status,
                displayStatus:
                  overviewApplication?.displayStatus ||
                  detailApplication?.displayStatus,
                location:
                  detailApplication?.location || overviewApplication?.location,
                progressSteps: overviewApplication?.progressSteps || [],
                bookingMeta: {
                  ...(detailApplication?.bookingMeta || {}),
                  ...(overviewApplication?.bookingMeta || {}),
                  expiredAt:
                    detailApplication?.bookingMeta?.expiredAt ||
                    overviewApplication?.bookingMeta?.expiredAt ||
                    '',
                  isScheduled:
                    detailApplication?.bookingMeta?.isScheduled === true,
                  isReschedule:
                    overviewApplication?.interviewInvitationIsReschedule ===
                      true ||
                    overviewApplication?.bookingMeta?.isReschedule === true,
                  interviewStage:
                    detailApplication?.bookingMeta?.interviewStage ||
                    overviewApplication?.bookingMeta?.interviewStage ||
                    overviewApplication?.interviewInvitationStage ||
                    overviewApplication?.interviewStage ||
                    '',
                },
                interview: detailApplication?.interview
                  ? {
                      ...detailApplication.interview,
                      stage:
                        detailApplication?.interview?.stage ||
                        overviewApplication?.interviewStage ||
                        overviewApplication?.interviewInvitationStage ||
                        '',
                    }
                  : overviewApplication?.interview,
                overviewMeta: overviewApplication,
              },
            ]
          } else {
            nextApplications = [detailApplication]
          }
        }
      }
    } else if (!userToken) {
      nextErrorState = {
        title: 'Oops!',
        desc: Lang(lang, 'applications_login_required'),
      }
    } else {
      const overviewResponse = await loadMyApplicationOverview({
        userToken,
      })

      if (!overviewResponse) {
        nextErrorState = {
          title: 'Oops!',
          desc: Lang(lang, 'applications_load_failed'),
        }
      } else {
        const overviewApplications = overviewResponse.map((item) =>
          mapCandidateToMyApplicationOverview(item, lang),
        )

        nextApplications = await Promise.all(
          overviewApplications.map(async (application) => {
            if (
              application?.kind === 'active' &&
              application?.interview?.isScheduled
            ) {
              return application
            }

            if (
              !application?.hasActiveInterviewInvitation ||
              application?.interviewInvitationID <= 0 ||
              String(application?.interviewInvitationStatus || '')
                .trim()
                .toLowerCase() !== 'available' ||
              String(application?.interviewInvitationEmailStatus || '')
                .trim()
                .toLowerCase() === 'pending' ||
              !userToken
            ) {
              return application
            }

            let detailResponse = null

            try {
              detailResponse = await loadMyApplicationDetail({
                interviewInvitationID: application.interviewInvitationID,
                userToken,
              })
            } catch {
              return application
            }

            if (!detailResponse) return application

            const detailApplication = mapInterviewBookingToMyApplication(
              detailResponse,
              lang,
              application.interviewInvitationID,
            )[0]

            if (!detailApplication) return application

            return {
              ...application,
              ...detailApplication,
              id: application?.id,
              title: application?.title,
              company: application?.company,
              status: application?.status,
              displayStatus:
                application?.displayStatus || detailApplication?.displayStatus,
              location: detailApplication?.location || application?.location,
              progressSteps: application?.progressSteps || [],
              bookingMeta: {
                ...(detailApplication?.bookingMeta || {}),
                ...(application?.bookingMeta || {}),
                expiredAt:
                  detailApplication?.bookingMeta?.expiredAt ||
                  application?.bookingMeta?.expiredAt ||
                  '',
                isScheduled:
                  detailApplication?.bookingMeta?.isScheduled === true,
                isReschedule:
                  application?.interviewInvitationIsReschedule === true ||
                  application?.bookingMeta?.isReschedule === true,
                interviewStage:
                  detailApplication?.bookingMeta?.interviewStage ||
                  application?.bookingMeta?.interviewStage ||
                  application?.interviewInvitationStage ||
                  application?.interviewStage ||
                  '',
              },
              interview: detailApplication?.interview
                ? {
                    ...detailApplication.interview,
                    stage:
                      detailApplication?.interview?.stage ||
                      application?.interviewStage ||
                      application?.interviewInvitationStage ||
                      '',
                  }
                : application?.interview,
              overviewMeta: application,
            }
          }),
        )
      }
    }

    if (userToken && nextApplications.length > 0) {
      nextApplications = await Promise.all(
        nextApplications.map(async (application) => {
          if (
            !isPendingJobOfferApplication(application) ||
            Number(application?.jobID) <= 0 ||
            Number(application?.candidateID) <= 0
          ) {
            return application
          }

          const jobOfferResponse = await loadMyCandidateJobOffer({
            jobID: application.jobID,
            candidateID: application.candidateID,
            userToken,
          })
          const jobOfferData = jobOfferResponse?.data || null

          if (!jobOfferData) {
            return {
              ...application,
              jobOffer: {
                isAvailable: false,
                status: '',
              },
            }
          }

          return {
            ...application,
            jobOffer: {
              ...jobOfferData,
              isAvailable: true,
            },
            preOnboarding: {
              ...(application?.preOnboarding || {}),
              expiresAt: jobOfferData?.preOnBoardingExpiredAt || '',
              expiryDate: jobOfferData?.preOnBoardingExpiredAt || '',
              submittedAt: jobOfferData?.preOnBoardingSubmittedAt || '',
              status: jobOfferData?.status || '',
            },
          }
        }),
      )
    }

    setApplications(nextApplications)
    setErrorState(nextErrorState)
    applyDefaultSelections(nextApplications)
    setActiveApplication(
      nextApplications.find((item) => item.kind === 'active') || null,
    )
    setIsLoading(false)
  }, [
    applyDefaultSelections,
    hasInterviewInvitationID,
    interviewInvitationID,
    lang,
    userToken,
  ])

  useEffect(() => {
    loadApplications()
  }, [loadApplications])

  useEffect(() => {
    const loadHolidays = async () => {
      if (!userToken) {
        setHolidayDates([])
        return
      }

      const startDate = new Date()
      const endDate = new Date(
        startDate.getFullYear(),
        startDate.getMonth() + 11,
        startDate.getDate(),
      )

      const items = await loadHolidayDates({
        startDate: toDateParam(startDate),
        endDate: toDateParam(endDate),
        userToken,
      })

      setHolidayDates(Array.isArray(items) ? items : [])
    }

    loadHolidays()
  }, [userToken])

  useEffect(() => {
    if (!applications.length) return

    const holidayDateSet = normalizeHolidayDateSet(holidayDates)
    const nextSelectedDateById = { ...selectedDateById }
    const nextSelectedSlotIdById = { ...selectedSlotIdById }
    let hasDateChange = false
    let hasSlotChange = false

    applications.forEach((application) => {
      const currentSelectedDate = nextSelectedDateById[application.id] || ''
      const currentSelectedSlotId = nextSelectedSlotIdById[application.id] || ''
      const currentDay =
        application?.interview?.schedule?.find(
          (day) => day.date === currentSelectedDate,
        ) || null
      const fallbackDay = isSelectableScheduleDay(currentDay, holidayDateSet)
        ? currentDay
        : getFirstSelectableScheduleDay(application, holidayDateSet)
      const fallbackSlot =
        fallbackDay?.slots?.find(
          (slot) => String(slot.id) === String(currentSelectedSlotId),
        ) || getFirstAvailableSlot(fallbackDay)
      const nextDate = fallbackDay?.date || ''
      const nextSlotId = fallbackSlot?.id || ''

      if (nextSelectedDateById[application.id] !== nextDate) {
        nextSelectedDateById[application.id] = nextDate
        hasDateChange = true
      }

      if (nextSelectedSlotIdById[application.id] !== nextSlotId) {
        nextSelectedSlotIdById[application.id] = nextSlotId
        hasSlotChange = true
      }
    })

    if (hasDateChange) {
      setSelectedDateById(nextSelectedDateById)
    }

    if (hasSlotChange) {
      setSelectedSlotIdById(nextSelectedSlotIdById)
    }
  }, [applications, holidayDates, selectedDateById, selectedSlotIdById])

  const handleDateChange = (applicationId, date) => {
    const targetApplication = applications.find(
      (item) => item.id === applicationId,
    )
    const holidayDateSet = normalizeHolidayDateSet(holidayDates)
    const selectedDay = targetApplication?.interview?.schedule?.find(
      (day) => day.date === date,
    )

    if (!isSelectableScheduleDay(selectedDay, holidayDateSet)) {
      return
    }

    const nextSlot = getFirstAvailableSlot(selectedDay)

    setSelectedDateById((prev) => ({
      ...prev,
      [applicationId]: date,
    }))

    setSelectedSlotIdById((prev) => ({
      ...prev,
      [applicationId]: nextSlot?.id || '',
    }))
  }

  const handleSlotChange = (applicationId, slotId) => {
    setSelectedSlotIdById((prev) => ({
      ...prev,
      [applicationId]: String(slotId),
    }))
  }

  const handleConfirm = async (application) => {
    if (submittingApplicationRef.current) return

    const selectedDate = selectedDateById[application.id] || ''
    const selectedSlotId = selectedSlotIdById[application.id] || ''
    const selectedDay = application?.interview?.schedule?.find(
      (day) => day.date === selectedDate,
    )
    const selectedSlot = selectedDay?.slots?.find(
      (slot) => String(slot.id) === String(selectedSlotId),
    )

    if (!selectedDay || !selectedSlot) {
      showStatusAlert({
        title: 'Error!',
        text: Lang(lang, 'select_interview_schedule_first'),
        icon: 'error',
        timer: 2500,
      })
      return
    }

    submittingApplicationRef.current = application.id
    setSubmittingApplicationId(application.id)

    let response

    try {
      response = await submitMyApplicationSchedule({
        interviewInvitationID: application?.bookingMeta?.interviewInvitationID,
        interviewSlotID: selectedSlot?.interviewSlotID || 0,
        userToken,
      })
    } finally {
      submittingApplicationRef.current = ''
      setSubmittingApplicationId('')
    }

    if (typeof response === 'string') {
      showStatusAlert({
        title: 'Error!',
        text: response,
        icon: 'error',
        timer: 3000,
      })
      return
    }

    await showStatusAlert({
      title: 'Success!',
      text: response?.message || Lang(lang, 'interview_slot_selected_success'),
      icon: 'success',
      timer: 1500,
    })

    if (hasInterviewInvitationID) {
      window.location.replace(window.location.pathname)
      return
    }

    await loadApplications()
  }

  const handleOpenReschedule = (application) => {
    if (
      application?.interview?.isScheduled ||
      application?.interview?.selectedDateTime
    ) {
      return
    }

    setActiveApplication(application)
    setIsRescheduleModalOpen(true)
  }

  const handleRescheduleSubmit = async ({ requestedDate, note }) => {
    if (!activeApplication) return
    if (reschedulingApplicationRef.current) return

    reschedulingApplicationRef.current = activeApplication.id
    setReschedulingApplicationId(activeApplication.id)

    let response

    try {
      response = await requestMyApplicationReschedule({
        interviewInvitationID:
          activeApplication?.bookingMeta?.interviewInvitationID,
        requestedDate,
        note,
        userToken,
      })
    } finally {
      reschedulingApplicationRef.current = ''
      setReschedulingApplicationId('')
    }

    if (typeof response === 'string') {
      showStatusAlert({
        title: 'Error!',
        text: response,
        icon: 'error',
        timer: 3000,
      })
      return
    }

    setIsRescheduleModalOpen(false)

    showStatusAlert({
      title: 'Success!',
      text:
        response?.message || Lang(lang, 'interview_reschedule_request_success'),
      icon: 'success',
      timer: 2500,
    })

    await loadApplications()
  }

  return (
    <>
      <div className='min-w-0'>
        <div>
          <div className='flex flex-wrap items-center gap-3 sm:gap-4'>
            <button
              type='button'
              onClick={() => setActiveTab('active')}
              className={`inline-flex h-[30px] items-center justify-center rounded-full border px-[13px] py-[5px] text-[14px] font-medium leading-4 transition ${
                activeTab === 'active'
                  ? 'border-[#E5E7EB] bg-[#EA2427] text-white'
                  : 'border-[#E5E7EB] bg-white text-[#111827] hover:bg-[#F9FAFB]'
              }`}
            >
              {Lang(lang, 'active_applications')} ({activeApplications.length})
            </button>

            <button
              type='button'
              onClick={() => setActiveTab('history')}
              className={`inline-flex h-[30px] items-center justify-center rounded-full border px-[13px] py-[5px] text-[14px] font-medium leading-4 transition ${
                activeTab === 'history'
                  ? 'border-[#E5E7EB] bg-[#EA2427] text-white'
                  : 'border-[#E5E7EB] bg-white text-[#111827] hover:bg-[#F9FAFB]'
              }`}
            >
              {Lang(lang, 'history')} ({historyApplications.length})
            </button>
          </div>

          <div className='mt-6'>
            <h3 className='text-[20px] font-medium leading-[28px] text-[#111827]'>
              {sectionTitle}
            </h3>
            <div className='mt-3 border-t border-[#E2E8F0]' />
          </div>

          <div className='mt-6 flex flex-col gap-5'>
            {isLoading ? (
              <div className='rounded-[24px] border border-[#E5E7EB] bg-white px-6 py-10 text-center text-[#6B7280]'>
                {Lang(lang, 'loading_applications')}
              </div>
            ) : errorState ? (
              <EmptyState title={errorState.title} desc={errorState.desc} />
            ) : visibleApplications.length === 0 ? (
              <EmptyState
                title={emptyStateContent.title}
                desc={emptyStateContent.desc}
              />
            ) : (
              visibleApplications.map((application) =>
                activeTab === 'history' ? (
                  <HistoryApplicationRenderer
                    key={application.id}
                    application={application}
                    lang={lang}
                  />
                ) : (
                  <ActiveApplicationRenderer
                    key={application.id}
                    application={application}
                    holidayDates={holidayDates}
                    lang={lang}
                    selectedDate={selectedDateById[application.id] || ''}
                    selectedSlotId={selectedSlotIdById[application.id] || ''}
                    isSubmitting={submittingApplicationId === application.id}
                    isRescheduling={
                      reschedulingApplicationId === application.id
                    }
                    userToken={userToken}
                    onDateChange={(date) =>
                      handleDateChange(application.id, date)
                    }
                    onSlotChange={(slotId) =>
                      handleSlotChange(application.id, slotId)
                    }
                    onConfirm={() => handleConfirm(application)}
                    onRefresh={loadApplications}
                    onReschedule={() => handleOpenReschedule(application)}
                  />
                ),
              )
            )}
          </div>
        </div>
      </div>

      <RescheduleModal
        open={isRescheduleModalOpen}
        lang={lang}
        initialSelectedDate={
          activeApplication ? selectedDateById[activeApplication.id] || '' : ''
        }
        holidayDates={holidayDates}
        blockedDateKeys={activeApplication?.interview?.slotDateKeys || []}
        onClose={() => setIsRescheduleModalOpen(false)}
        onSubmit={handleRescheduleSubmit}
      />
    </>
  )
}

export default MyApplicationContent
